import React from "react";
import { Section, Reveal, Icon } from "../components/primitives.jsx";
import { ArticleCard } from "../components/blocks.jsx";
import { PageHero, HeroArt } from "../components/visuals.jsx";
import { ArticleReader } from "../components/ArticleReader.jsx";
import { getArticleItems } from "../lib/content.js";

const PAGE_SIZE = 10;

export function Articles({ go, c, locale = "en", radius = 16 }) {
  const t = c.articlesPage;
  const [page, setPage] = React.useState(1);
  const [items, setItems] = React.useState(t.items || []);
  const [total, setTotal] = React.useState(t.total || t.items?.length || 0);
  const [loading, setLoading] = React.useState(false);
  const [openArticle, setOpenArticle] = React.useState(null);
  const [feat, ...rest] = items;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const pageItems = getPaginationItems(page, totalPages);
  // Directus articles navigate to their own page; static items open the modal.
  const open = (a) => (a.slug ? go(a.slug) : setOpenArticle(a));

  React.useEffect(() => {
    setPage(1);
    setItems(t.items || []);
    setTotal(t.total || t.items?.length || 0);
  }, [locale, t.items, t.total]);

  React.useEffect(() => {
    let cancelled = false;
    setLoading(true);
    getArticleItems(locale, { page, limit: PAGE_SIZE })
      .then((result) => {
        if (cancelled) return;
        setItems(result.items);
        setTotal(result.total);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [locale, page]);

  if (!feat) {
    return (
      <main style={{ fontFamily: "var(--font-article)" }}>
        <PageHero eyebrow={t.eyebrow} title={t.title} lede={t.lede} radius={radius} art={<HeroArt kind="articles" radius={radius} />} fontFamily="var(--font-article)" />
      </main>
    );
  }

  return (
    <main style={{ fontFamily: "var(--font-article)" }}>
      <PageHero eyebrow={t.eyebrow} title={t.title} lede={t.lede} radius={radius} art={<HeroArt kind="articles" radius={radius} />} fontFamily="var(--font-article)" />

      <Section tone="wash">
        <Reveal>
          <div
            onClick={() => open(feat)}
            role="button"
            tabIndex={0}
            aria-label={feat.title}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); open(feat); } }}
            className="rw-feat"
            style={{ display: "grid", gridTemplateColumns: "1.08fr 1fr", gap: 0, overflow: "hidden", borderRadius: radius + 4, border: "1px solid var(--border-subtle)", boxShadow: "var(--shadow-sm)", background: "var(--surface-card)", cursor: "pointer", marginBottom: "var(--space-7)", opacity: loading ? 0.72 : 1, transition: "opacity var(--duration-base) var(--ease-out)" }}
          >
            <div style={{ background: "var(--brand-ink)", minHeight: 340, position: "relative", overflow: "hidden", padding: "var(--space-7)", display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
              {feat.image ? (
                <img src={feat.image} alt="" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
              ) : (
                <div style={{ position: "absolute", inset: 0, opacity: 0.5, background: "repeating-linear-gradient(135deg, transparent 0 18px, rgba(255,255,255,0.04) 18px 19px)" }} />
              )}
              <div style={{ position: "absolute", inset: 0, background: feat.image ? "linear-gradient(180deg, rgba(17,17,19,0.05), rgba(17,17,19,0.72))" : "transparent" }} />
              <div aria-hidden="true" style={{ position: "absolute", right: -30, top: -30, fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 220, color: "rgba(238,0,3,0.16)", lineHeight: 1 }}>&ldquo;</div>
              <span style={{ position: "relative", alignSelf: "flex-start", fontSize: "var(--text-2xs)", fontWeight: 700, letterSpacing: "0.08em", textTransform: "uppercase", color: "#fff", background: "var(--brand-red)", padding: "5px 10px", borderRadius: "var(--radius-pill)" }}>{feat.tag}</span>
            </div>
            <div style={{ padding: "var(--space-8)", display: "flex", flexDirection: "column", justifyContent: "center" }}>
              <div style={{ display: "flex", gap: 10, fontSize: "var(--text-xs)", color: "var(--text-muted)", marginBottom: 12 }}>
                <span>{feat.date}</span>
                <span>·</span>
                <span>{feat.read}</span>
              </div>
              <h2 style={{ margin: 0, fontFamily: "var(--font-article)", fontWeight: 800, fontSize: "var(--text-xl)", lineHeight: 1.15, letterSpacing: "-0.02em", color: "var(--brand-ink)", textWrap: "balance" }}>{feat.title}</h2>
              <p style={{ margin: "14px 0 0", fontSize: "var(--text-md)", lineHeight: 1.6, color: "var(--text-body)" }}>{feat.excerpt}</p>
              <span style={{ marginTop: 20, display: "inline-flex", alignItems: "center", gap: 7, fontSize: "var(--text-sm)", fontWeight: 700, color: "var(--brand-red)" }}>{t.readMore} <Icon name="arrow-right" size={16} /></span>
            </div>
          </div>
        </Reveal>

        <div className="rw-svc-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: "var(--space-5)" }}>
          {rest.map((a, i) => (
            <Reveal key={a.slug || a.title} delay={(i % 3) * 70}><ArticleCard a={a} radius={radius} label={t.readMore} onClick={() => open(a)} /></Reveal>
          ))}
        </div>

        {totalPages > 1 && (
          <nav aria-label="Article pages" style={{ marginTop: "var(--space-7)", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap" }}>
            <PageButton disabled={page === 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))} label="Previous" icon="arrow-left" />
            {pageItems.map((value, index) => (
              value === "…" ? (
                <span key={`${value}-${index}`} aria-hidden="true" style={{ width: 24, textAlign: "center", color: "var(--text-muted)", fontWeight: 800 }}>
                  {value}
                </span>
              ) : (
              <button
                key={value}
                type="button"
                onClick={() => setPage(value)}
                aria-current={value === page ? "page" : undefined}
                disabled={loading}
                style={{
                  width: 40,
                  height: 40,
                  borderRadius: "var(--radius-sm)",
                  border: value === page ? "1px solid var(--brand-red)" : "1px solid var(--border-subtle)",
                  background: value === page ? "var(--brand-red)" : "var(--surface-card)",
                  color: value === page ? "#fff" : "var(--brand-ink)",
                  fontWeight: 800,
                  cursor: loading ? "wait" : "pointer"
                }}
              >
                {value}
              </button>
              )
            ))}
            <PageButton disabled={page === totalPages || loading} onClick={() => setPage((value) => Math.min(totalPages, value + 1))} label="Next" icon="arrow-right" />
          </nav>
        )}
      </Section>

      <ArticleReader article={openArticle} labels={{ close: t.close, readerNote: t.readerNote, talk: t.talk }} onClose={() => setOpenArticle(null)} go={go} />
    </main>
  );
}

function getPaginationItems(page, totalPages) {
  if (totalPages <= 7) return Array.from({ length: totalPages }, (_, index) => index + 1);

  const pages = new Set([1, totalPages, page - 1, page, page + 1]);
  if (page <= 3) {
    pages.add(2);
    pages.add(3);
    pages.add(4);
  }
  if (page >= totalPages - 2) {
    pages.add(totalPages - 1);
    pages.add(totalPages - 2);
    pages.add(totalPages - 3);
  }

  const ordered = [...pages]
    .filter((value) => value >= 1 && value <= totalPages)
    .sort((a, b) => a - b);

  return ordered.flatMap((value, index) => {
    const previous = ordered[index - 1];
    return previous && value - previous > 1 ? ["…", value] : [value];
  });
}

function PageButton({ disabled, onClick, label, icon }) {
  const isPrevious = icon === "arrow-left";
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      style={{
        minWidth: 44,
        height: 40,
        padding: "0 12px",
        borderRadius: "var(--radius-sm)",
        border: "1px solid var(--border-subtle)",
        background: "var(--surface-card)",
        color: disabled ? "var(--text-subtle)" : "var(--brand-ink)",
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 7,
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: 700
      }}
      aria-label={label}
    >
      {isPrevious && <Icon name={icon} size={16} />}
      <span>{label}</span>
      {!isPrevious && <Icon name={icon} size={16} />}
    </button>
  );
}
